import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  userApi, type InvitedUser, type UserRole, type EmailProvider,
} from '@/lib/api';
import {
  UserPlus, Trash2, Shield, Clock, CheckCircle2,
  ChevronDown, Mail, Globe, AlertTriangle, X, Users, Pencil, Check,
} from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SettingsEmptyState } from '@/components/SettingsEmptyState';
import { useNotification } from '@/contexts/NotificationContext';

export const Route = createFileRoute('/_auth/settings/members')({
  component: MembersPage,
});

const ROLES: UserRole[] = ['ADMIN', 'DEVELOPER', 'OPERATOR', 'VIEWER'];

const ROLE_META: Record<UserRole, { label: string; color: string; desc: string }> = {
  ADMIN:     { label: 'Admin',     color: '#a78bfa', desc: 'Full access, invite & remove users' },
  DEVELOPER: { label: 'Developer', color: '#60a5fa', desc: 'Read/write access, no user management' },
  OPERATOR:  { label: 'Operator',  color: '#34d399', desc: 'Can manage handoffs and conversations' },
  VIEWER:    { label: 'Viewer',    color: '#94a3b8', desc: 'Read-only access' },
};

const PROVIDER_META: Record<EmailProvider, { label: string; color: string; warning?: string }> = {
  GOOGLE:    { label: '🟢 Google Workspace detected', color: '#34d399' },
  MICROSOFT: { label: '🔵 Microsoft 365 detected — not yet supported', color: '#60a5fa', warning: "Microsoft login is coming soon. This user won't be able to sign in yet." },
  UNKNOWN:   { label: '🟡 Provider unknown', color: '#fbbf24', warning: "We couldn't detect the email provider. Make sure the user has a Google account." },
};

function getInitials(user: InvitedUser): string {
  const src = user.name ?? user.email;
  return src.split(/[\s@.]+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 8,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  color: 'var(--color-text)',
  fontSize: 13, width: '100%',
  outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600,
  color: 'var(--color-muted)', textTransform: 'uppercase',
  letterSpacing: '0.06em', marginBottom: 6,
};

const ghostBtnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '6px 10px', borderRadius: 7,
  border: '1px solid var(--border)',
  background: 'transparent', cursor: 'pointer',
  fontSize: 12, color: 'var(--color-muted)',
  transition: 'all 0.15s',
};

export default function MembersPage() {
  const { success, error: notifyError } = useNotification();
  const [users, setUsers] = useState<InvitedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<InvitedUser | null>(null);

  // Invite form
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<UserRole>('VIEWER');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Live provider detection
  const [detecting, setDetecting] = useState(false);
  const [detectedProvider, setDetectedProvider] = useState<EmailProvider | null>(null);
  const detectTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    userApi.list().then(setUsers).catch(() => notifyError('Failed to load users', '')).finally(() => setLoading(false));
  }, [notifyError]);

  useEffect(() => { load(); }, [load]);

  // Debounced provider detection
  useEffect(() => {
    if (detectTimeout.current) clearTimeout(detectTimeout.current);
    setDetectedProvider(null);
    if (!email.includes('@') || email.split('@')[1]?.length < 3) return;
    detectTimeout.current = setTimeout(async () => {
      setDetecting(true);
      try {
        const res = await userApi.detectProvider(email);
        setDetectedProvider(res.provider);
      } catch {
        setDetectedProvider('UNKNOWN');
      } finally {
        setDetecting(false);
      }
    }, 700);
    return () => { if (detectTimeout.current) clearTimeout(detectTimeout.current); };
  }, [email]);

  async function handleInvite() {
    setInviteError(null);
    setSaving(true);
    try {
      const res = await userApi.invite({ email, role, name: name || undefined });
      setUsers(prev => [...prev, res.user]);
      success('Invite created', `${res.user.email} will activate on first sign-in.`);
      setEmail(''); setName(''); setRole('VIEWER');
      setDetectedProvider(null);
      setShowInvite(false);
    } catch (e: any) {
      setInviteError(e.message ?? 'Failed to invite user.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove() {
    if (!removeTarget) return;
    try {
      await userApi.remove(removeTarget.id);
      setUsers(prev => prev.filter(u => u.id !== removeTarget.id));
      success('Member removed', `${removeTarget.email} no longer has access.`);
    } catch (e: any) {
      notifyError('Failed to remove member', e.message ?? '');
    } finally {
      setRemoveTarget(null);
    }
  }

  async function handleRoleChange(userId: string, newRole: UserRole) {
    try {
      const updated = await userApi.updateRole(userId, newRole);
      setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
      success('Role updated');
    } catch (e: any) {
      notifyError('Failed to update role', e.message ?? '');
    }
  }

  // Stats
  const active = users.filter(u => u.active).length;
  const pending = users.filter(u => !u.active).length;

  return (
    <div className="page">

      {/* ── Header ── */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: -0.5 }}>Team Members</h1>
            <p style={{ color: 'var(--color-muted)', fontSize: 13, marginTop: 6 }}>
              Invite colleagues and manage their access roles.
            </p>
          </div>
          <button
            onClick={() => { setShowInvite(true); setInviteError(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: 'var(--color-primary)', color: '#fff',
              fontWeight: 600, fontSize: 14, cursor: 'pointer',
              boxShadow: '0 4px 16px rgba(99,102,241,0.3)',
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(99,102,241,0.4)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 16px rgba(99,102,241,0.3)'; }}
          >
            <UserPlus size={15} /> Invite member
          </button>
        </div>

        {/* Stats row */}
        {users.length > 0 && (
          <div style={{ display: 'flex', gap: 16, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Total', value: users.length, color: '#6366f1' },
              { label: 'Active', value: active, color: '#10b981' },
              { label: 'Pending', value: pending, color: '#f59e0b' },
              { label: 'Admins', value: users.filter(u => u.role === 'ADMIN').length, color: '#a78bfa' },
            ].map(s => (
              <div key={s.label} style={{
                padding: '10px 16px', borderRadius: 10,
                background: `${s.color}10`, border: `1px solid ${s.color}25`,
                minWidth: 80,
              }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'var(--color-muted)', marginTop: 1 }}>{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Content ── */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ height: 70, borderRadius: 14, background: 'var(--surface-2)', opacity: 0.4, animation: 'pulse 1.5s ease infinite' }} />
          ))}
        </div>
      ) : users.length === 0 ? (
        <SettingsEmptyState
          icons={[
            { icon: <Users size={18} />, color: '#6366f1' },
            { icon: <UserPlus size={18} />, color: '#8b5cf6' },
          ]}
          title="No team members yet"
          description="Invite colleagues by email. They'll receive instructions to sign in with their Google account and activate their access."
          buttonLabel="Invite first member"
          onAction={() => { setShowInvite(true); setInviteError(null); }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {users.map(user => (
            <MemberRow
              key={user.id}
              user={user}
              onRoleChange={handleRoleChange}
              onNameChange={(id, name) => {
                userApi.update(id, { name })
                  .then(updated => {
                    setUsers(prev => prev.map(u => u.id === updated.id ? updated : u));
                    success('Name updated');
                  })
                  .catch((e: any) => notifyError('Failed to update name', e.message ?? ''));
              }}
              onRemove={setRemoveTarget}
            />
          ))}
        </div>
      )}

      {/* ── Invite modal ── */}
      {showInvite && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)', zIndex: 100,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--color-surface)', border: '1px solid var(--border)',
            borderRadius: 16, width: 480, maxWidth: 'calc(100vw - 2rem)',
            overflow: 'hidden',
          }}>
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '18px 24px', borderBottom: '1px solid var(--border)',
            }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                <UserPlus size={16} style={{ color: 'var(--color-primary)' }} /> Invite a member
              </h3>
              <button onClick={() => setShowInvite(false)} style={ghostBtnStyle}>
                <X size={14} />
              </button>
            </div>

            {/* Modal body */}
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Email */}
              <div>
                <label style={labelStyle}><Mail size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> Email address</label>
                <input
                  style={inputStyle}
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoFocus
                />
                {/* Provider badge */}
                {detecting && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 12, color: 'var(--color-muted)' }}>
                    <div style={{ width: 10, height: 10, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
                    Detecting provider…
                  </div>
                )}
                {!detecting && detectedProvider && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 12, fontWeight: 600, color: PROVIDER_META[detectedProvider].color }}>
                    <Globe size={11} /> {PROVIDER_META[detectedProvider].label}
                  </div>
                )}
                {detectedProvider && PROVIDER_META[detectedProvider].warning && (
                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 7, marginTop: 7,
                    padding: '8px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.4,
                    background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)',
                    color: '#fbbf24',
                  }}>
                    <AlertTriangle size={12} style={{ marginTop: 1, flexShrink: 0 }} />
                    {PROVIDER_META[detectedProvider].warning}
                  </div>
                )}
              </div>

              {/* Display name */}
              <div>
                <label style={labelStyle}>Display name <span style={{ opacity: 0.5, textTransform: 'none', fontWeight: 400 }}>(optional)</span></label>
                <input
                  style={inputStyle}
                  type="text"
                  placeholder="Alice Smith"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>

              {/* Role */}
              <div>
                <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Shield size={11} /> Role
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {ROLES.map(r => (
                    <button
                      key={r}
                      onClick={() => setRole(r)}
                      style={{
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                        gap: 3, padding: '10px 12px',
                        background: role === r ? `${ROLE_META[r].color}10` : 'var(--surface-2)',
                        border: `1px solid ${role === r ? ROLE_META[r].color : 'var(--border)'}`,
                        borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.15s',
                        color: role === r ? ROLE_META[r].color : 'var(--color-muted)',
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 700 }}>{ROLE_META[r].label}</span>
                      <span style={{ fontSize: 11, color: 'var(--color-muted)', lineHeight: 1.3 }}>{ROLE_META[r].desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {inviteError && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 7,
                  padding: '10px 12px', borderRadius: 8,
                  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)',
                  color: '#ef4444', fontSize: 13,
                }}>
                  <AlertTriangle size={13} style={{ marginTop: 1, flexShrink: 0 }} /> {inviteError}
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div style={{
              display: 'flex', justifyContent: 'flex-end', gap: 8,
              padding: '14px 24px', borderTop: '1px solid var(--border)',
              background: 'var(--surface-2)',
            }}>
              <button onClick={() => setShowInvite(false)} style={ghostBtnStyle}>Cancel</button>
              <button
                onClick={handleInvite}
                disabled={!email || saving}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 18px', borderRadius: 8, border: 'none',
                  background: 'var(--color-primary)', color: '#fff',
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  opacity: !email || saving ? 0.5 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                <UserPlus size={13} /> {saving ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!removeTarget}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        title={`Remove "${removeTarget?.name ?? removeTarget?.email}"?`}
        description={`${removeTarget?.name ?? removeTarget?.email} will lose access immediately. This cannot be undone.`}
        confirmLabel="Remove member"
        variant="danger"
      />

      <style>{`
        @keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 0.7; } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ── Member Row ────────────────────────────────────────────────────────────────

function MemberRow({
  user, onRoleChange, onNameChange, onRemove,
}: {
  user: InvitedUser;
  onRoleChange: (id: string, role: UserRole) => void;
  onNameChange: (id: string, name: string) => void;
  onRemove: (user: InvitedUser) => void;
}) {
  const [roleOpen, setRoleOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal] = useState(user.name ?? '');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const meta = ROLE_META[user.role as UserRole] ?? ROLE_META.VIEWER;

  function startEdit() {
    setNameVal(user.name ?? '');
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.focus(), 50);
  }

  function commitEdit() {
    if (nameVal.trim() && nameVal.trim() !== user.name) {
      onNameChange(user.id, nameVal.trim());
    }
    setEditingName(false);
  }

  return (
    <div
      style={{
        background: 'var(--surface-2)', border: '1px solid var(--border)',
        borderRadius: 14, overflow: 'visible',
        display: 'flex', alignItems: 'center', padding: '14px 20px', gap: 16,
        transition: 'border-color 0.2s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#6366f155'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      {/* Avatar */}
      <div style={{
        width: 38, height: 38, borderRadius: 10, flexShrink: 0,
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, color: '#fff',
        boxShadow: '0 2px 6px rgba(99,102,241,0.25)',
      }}>
        {getInitials(user)}
      </div>

      {/* Name + email */}
      <div style={{ flex: 2, minWidth: 0 }}>
        {editingName ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              ref={nameInputRef}
              value={nameVal}
              onChange={e => setNameVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditingName(false); }}
              style={{
                flex: 1, padding: '4px 8px', borderRadius: 6, fontSize: 13,
                background: 'var(--surface-2)', border: '1px solid #6366f1',
                color: 'var(--color-text)', outline: 'none', minWidth: 0,
              }}
            />
            <button
              onClick={commitEdit}
              style={{ ...ghostBtnStyle, padding: '4px 8px', borderColor: '#34d39940', color: '#34d399' }}
              title="Save"
            >
              <Check size={13} />
            </button>
            <button
              onClick={() => setEditingName(false)}
              style={{ ...ghostBtnStyle, padding: '4px 8px' }}
              title="Cancel"
            >
              <X size={13} />
            </button>
          </div>
        ) : (
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
            onClick={startEdit}
            title="Click to edit name"
          >
            <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.name ?? <span style={{ color: 'var(--color-muted)', fontStyle: 'italic' }}>No name</span>}
            </span>
            <Pencil size={11} style={{ color: 'var(--color-muted)', flexShrink: 0, opacity: 0.6 }} />
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--color-muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {user.email}
        </div>
      </div>

      {/* Role chip */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <button
          onClick={() => setRoleOpen(o => !o)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 20,
            border: `1px solid ${meta.color}40`, background: `${meta.color}10`,
            color: meta.color, fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}
        >
          {meta.label} <ChevronDown size={11} />
        </button>
        {roleOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50,
            background: 'var(--color-surface)', border: '1px solid var(--border)',
            borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            overflow: 'hidden', minWidth: 140,
          }}>
            {(['ADMIN', 'DEVELOPER', 'OPERATOR', 'VIEWER'] as UserRole[]).map(r => (
              <button
                key={r}
                onClick={() => { onRoleChange(user.id, r); setRoleOpen(false); }}
                style={{
                  width: '100%', padding: '9px 14px',
                  background: user.role === r ? `${ROLE_META[r].color}10` : 'none',
                  border: 'none', textAlign: 'left', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  fontSize: 13, color: user.role === r ? ROLE_META[r].color : 'var(--color-text)',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={e => { if (user.role !== r) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = user.role === r ? `${ROLE_META[r].color}10` : 'none'; }}
              >
                {ROLE_META[r].label}
                {user.role === r && <span style={{ marginLeft: 'auto', fontSize: 10 }}>✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Status badge */}
      <div style={{ flexShrink: 0 }}>
        {user.active ? (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 9px', borderRadius: 20,
            background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)',
            color: '#34d399', fontSize: 11, fontWeight: 600,
          }}>
            <CheckCircle2 size={11} /> Active
          </span>
        ) : (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '3px 9px', borderRadius: 20,
            background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)',
            color: '#fbbf24', fontSize: 11, fontWeight: 600,
          }}>
            <Clock size={11} /> Pending
          </span>
        )}
      </div>

      {/* Last login */}
      <div style={{ flexShrink: 0, minWidth: 90, fontSize: 12, color: 'var(--color-muted)', lineHeight: 1.4 }}>
        {user.lastLoginAt ? (
          <>
            <div>{new Date(user.lastLoginAt).toLocaleDateString()}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>{new Date(user.lastLoginAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
          </>
        ) : '—'}
      </div>

      {/* Delete */}
      <button
        onClick={() => onRemove(user)}
        style={{ ...ghostBtnStyle, marginLeft: 'auto', borderColor: 'transparent' }}
        onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'rgba(239,68,68,0.08)'; }}
        onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-muted)'; e.currentTarget.style.background = 'transparent'; }}
        title="Remove member"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}
